import React, { useState } from 'react';
import { Mail, Bell, CheckCircle } from 'lucide-react';
import { newsletterService } from '../lib/database';
import toast from 'react-hot-toast';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      toast.error('Please enter your email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      await newsletterService.subscribe(trimmedEmail);
      toast.success('🎉 Thank you for subscribing! Welcome to our community.');
      setEmail('');
      setSubscribed(true);
      
      // Reset subscribed state after 3 seconds
      setTimeout(() => setSubscribed(false), 3000);
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      
      if (error.message === 'Email already subscribed') {
        toast.error('📧 This email is already subscribed to our newsletter');
      } else {
        toast.error('❌ Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="newsletter-bg py-5">
      <div className="container">
        <div className="row justify-content-center px-2 px-md-0">
          <div className="col-lg-10 col-xl-8 text-center text-white">
            <div className="mb-4">
              <Bell size={40} className="mb-3" />
            </div>
            <h2 className="h1 fw-bold mb-3 mb-md-4" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>
              Stay Updated with Grad Vikalp
            </h2>
            <p className="lead text-blue-100 mb-4 mb-md-5 px-2" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
              Get the latest college updates, cutoffs, admission notifications, and expert guidance delivered to your inbox!
            </p>

            <div className="newsletter-form">
              {subscribed ? (
                <div className="alert alert-success d-inline-flex align-items-center gap-2 bg-white text-success border-0 shadow-lg mx-2">
                  <CheckCircle size={20} />
                  <span className="fw-semibold">Successfully subscribed!</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="row g-3 justify-content-center align-items-end px-2">
                    <div className="col-12 col-md-8 col-lg-7">
                      <div className="position-relative">
                        <Mail className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={20} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          className="form-control form-control-lg ps-5 border-0 shadow-sm w-100"
                          required
                          disabled={loading}
                          autoComplete="email"
                          style={{ 
                            borderRadius: '12px',
                            fontSize: '16px', // Prevents zoom on iOS
                            minHeight: '50px'
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-4 col-lg-5">
                      <button
                        type="submit"
                        disabled={loading || !email.trim()}
                        className="btn btn-light btn-lg w-100 fw-semibold shadow-sm position-relative"
                        style={{ 
                          borderRadius: '12px',
                          minHeight: '50px'
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Subscribing...
                          </>
                        ) : (
                          'Subscribe'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            <div className="mt-4 text-blue-100 small px-2">
              <p className="mb-2">Join students who trust us for college guidance</p>
              <div className="d-flex flex-wrap justify-content-center gap-2 gap-md-3 small">
                <span>✓ Weekly updates</span>
                <span>✓ Admission alerts</span>
                <span>✓ Expert tips</span>
                <span>✓ No spam</span>
              </div>
              <p className="mt-3 opacity-75" style={{ fontSize: '0.8rem' }}>
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;